import { Navigate } from 'react-router-dom';
import { BrandLoader } from './ui/BrandLoader';
import { useHourlyPayAccess, useHourlyPayFlagsSettled } from '../utils/hourlyPayAccess';

interface HourlyPayRouteProps {
  /**
   * `company`         — the company's hourly-pay switch is on (approver surfaces).
   * `hourlyEmployee`  — the switch is on AND the signed-in user's pay basis is Hourly (My shifts).
   */
  require: 'company' | 'hourlyEmployee';
  children: React.ReactElement;
}

/**
 * Feature guard for the hourly-pay routes (docs/hourly-pay-spec.md §7.2: hourly surfaces show only
 * when `payBasis` = Hourly AND `hourlyPayEnabled`; a server that sends neither keeps them dark).
 *
 * Same shape as ShiftSwapEnabledRoute: UX only — the server is the boundary (`My` returns only the
 * caller's own items, `Queue` only what the caller may see). Three states, never two: until a LIVE
 * GetUserInfo has confirmed the flags this page load it waits (unless the cache already allows), bounded by
 * HOURLY_FLAGS_MAX_WAIT_MS, rather than bouncing a returning user off a deep link. The flags are
 * filled by the boot/revalidate GetUserInfo, which every app load already makes.
 *
 * Compose INSIDE RoleProtectedRoute: role first, feature second.
 */
export const HourlyPayRoute = ({ require, children }: HourlyPayRouteProps) => {
  const access = useHourlyPayAccess();
  // A cached "allowed" admits at once; a cached "not allowed" waits for the live answer, so a deep
  // link into a company that switched hourly pay on while the app was closed is not bounced.
  const settled = useHourlyPayFlagsSettled(access.live);
  const allowed = require === 'company' ? access.companyEnabled : access.showMyShifts;

  if (allowed) return children;
  if (!settled) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <BrandLoader variant="inline" size="md" />
      </div>
    );
  }
  return <Navigate to="/" replace />;
};

export default HourlyPayRoute;
